/**
 * Titel und Meta-Description einer Event-Seite, wie sie im Google-Ergebnis stehen.
 *
 * Gegenstück zu `SeoSnippet.kt` im Backend: der Bot bekommt dort das Server-HTML
 * (`PreviewController`), nach dem Rendern setzt `EventDetail` dieselbe Fassung per Helmet.
 * Laufen beide auseinander, sieht Google zwei verschiedene Seiten.
 *
 * Warum Fakten statt Namen: bei den großen Veranstaltungen stehen wir auf Platz 5–8 und werden
 * kaum geklickt (Trello HGeTp8qd). Das Snippet soll die Frage des Suchenden schon beantworten —
 * Termin, Ort, Strecken, Höhenmeter, Anmeldestand. Teile werden nach Nutzen angehängt, solange sie
 * in die Längengrenze passen; was fehlt oder nicht passt, fällt weg, statt abgeschnitten zu werden.
 */
import { orderedVariantLabels, isTimeVariant } from './utils';
import { displayBundesland } from './brand';
import { seasonLongLabel } from './permanentSeason';
import type { RegistrationStatus } from './types';

export const SEO_TITLE_MAX = 60;
export const SEO_DESCRIPTION_MAX = 155;
const BRAND = ' | Radsport Events';
/** Index = `Date.getDay()`, also Sonntag zuerst. */
const WEEKDAYS = ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'];

export interface SeoSnippetEvent {
  title: string;
  city: string;
  /** Gebiet eines permanenten Events ohne Ort — steht dann an der Stelle der Stadt. */
  areaName?: string | null;
  bundesland?: string | null;
  /** ISO-Tag `YYYY-MM-DD`. */
  eventDate: string;
  endDate?: string | null;
  organizer?: string | null;
  distances?: { distanceKm: number; elevationMeters?: number | null; durationMinutes?: number | null }[];
  cancelled?: boolean;
  fullyBooked?: boolean;
  tentative?: boolean;
  /** Permanentes Event: kein Jahr im Namen, kein Termin im Snippet, dafür die Saison. */
  permanent?: boolean;
  seasonFrom?: number | null;
  seasonUntil?: number | null;
  registrationStatus?: RegistrationStatus;
}

interface Day { y: number; m: number; d: number }

function parseDay(iso: string): Day {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return { y, m, d };
}

const dd = (n: number) => String(n).padStart(2, '0');
const weekday = (day: Day) => WEEKDAYS[new Date(day.y, day.m - 1, day.d).getDay()];

function lastDay(e: SeoSnippetEvent): Day | null {
  if (!e.endDate || e.endDate.slice(0, 10) <= e.eventDate.slice(0, 10)) return null;
  return parseDay(e.endDate);
}

/** Hängt die Teile der Reihe nach an, überspringt, was nicht mehr passt. Null, wenn keiner passt. */
function fill(prefix: string, parts: string[], separator: string, max: number): string | null {
  const taken: string[] = [];
  for (const part of parts) {
    if ((prefix + [...taken, part].join(separator)).length <= max) taken.push(part);
  }
  return taken.length ? prefix + taken.join(separator) : null;
}

/** "Cyclassics" wird "Cyclassics 2026" — gesucht wird mit Jahreszahl. */
function nameWithYear(title: string, year: number): string {
  const trimmed = title.trim();
  return trimmed.includes(String(year)) ? trimmed : `${trimmed} ${year}`;
}

/** "30.08." bzw. "29.–31.08." / "30.08.–01.09." — das Jahr steht schon im Namen. */
function shortDate(start: Day, end: Day | null): string {
  const s = `${dd(start.d)}.${dd(start.m)}.`;
  if (!end) return s;
  const e = `${dd(end.d)}.${dd(end.m)}.`;
  return start.m === end.m ? `${dd(start.d)}.–${e}` : `${s}–${e}`;
}

/** "Sa., 16.08.2026" bzw. "Fr., 29.08. – So., 31.08.2026". */
function longDate(start: Day, end: Day | null): string {
  const full = (x: Day) => `${weekday(x)}, ${dd(x.d)}.${dd(x.m)}.${x.y}`;
  if (!end) return full(start);
  const first = start.y === end.y ? `${weekday(start)}, ${dd(start.d)}.${dd(start.m)}.` : full(start);
  return `${first} – ${full(end)}`;
}

function nonEmpty(s?: string | null): string | null {
  const t = (s ?? '').trim();
  return t ? t : null;
}

/** Stadt, bei einem permanenten Event ohne Ort sein Gebiet (wie `SeoSnippet.placeOf`). */
function placeOf(e: SeoSnippetEvent): string | null {
  return nonEmpty(e.city) ?? nonEmpty(e.areaName);
}

function location(e: SeoSnippetEvent): string | null {
  const parts = [placeOf(e), nonEmpty(displayBundesland(e.bundesland))].filter((p): p is string => !!p);
  return parts.length ? parts.join(', ') : null;
}

function statusLabel(e: SeoSnippetEvent): string | null {
  if (e.cancelled) return 'Abgesagt';
  if (e.fullyBooked) return 'Ausgebucht';
  if (e.tentative) return 'Termin vorläufig';
  if (e.permanent) {
    return `Permanent, ${seasonLongLabel(e)}`;
  }
  return null;
}

/** Bis drei Strecken einzeln, darüber als Spanne — sonst frisst die Liste die Beschreibung. */
function distancesLabel(e: SeoSnippetEvent): string | null {
  const distances = e.distances ?? [];
  const labels = orderedVariantLabels(distances);
  if (!labels.length) return null;
  if (labels.length <= 3) return `Strecken: ${labels.join(', ')}`;
  const km = distances.filter(d => !isTimeVariant(d) && d.distanceKm > 0).map(d => d.distanceKm);
  const timeLabels = labels.filter(l => !l.endsWith(' km'));
  const range = km.length >= 2 ? [`${Math.min(...km)}–${Math.max(...km)} km`] : [];
  return `Strecken: ${[...range, ...timeLabels].join(', ')}`;
}

function thousands(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** "2.300 Hm", bei mehreren Strecken mit verschiedenen Werten "bis 5.500 Hm". */
function elevationLabel(e: SeoSnippetEvent): string | null {
  const values = (e.distances ?? []).map(d => d.elevationMeters ?? 0).filter(v => v > 0);
  const distinct = values.filter((v, i) => values.indexOf(v) === i);
  if (!distinct.length) return null;
  const formatted = thousands(Math.max(...distinct));
  return distinct.length > 1 ? `bis ${formatted} Hm` : `${formatted} Hm`;
}

/**
 * Der öffentliche Anmeldestand — dieselben Worte wie das Badge (`registrationBadge`). Der
 * Anmeldebeginn selbst bleibt intern; Startgebühr oder Anmeldeschluss führt das Datenmodell
 * (noch) nicht.
 */
function registrationLabel(e: SeoSnippetEvent): string | null {
  if (e.cancelled || e.fullyBooked) return null;
  switch (e.registrationStatus) {
    case 'OPEN': return 'Anmeldung offen';
    case 'OPENING_SOON': return 'Anmeldung bald';
    case 'CLOSED': return 'Anmeldung geschlossen';
    case 'ONSITE_ONLY': return 'Anmeldung vor Ort';
    default: return null;
  }
}

export function eventSeoTitle(e: SeoSnippetEvent): string {
  const start = parseDay(e.eventDate);
  // Ein permanentes Event hat weder Jahr noch Termin: statt "30.08." steht "permanent".
  const name = e.permanent ? e.title.trim() : nameWithYear(e.title, start.y);
  const facts = [e.permanent ? 'permanent' : shortDate(start, lastDay(e)), placeOf(e), elevationLabel(e)]
    .filter((p): p is string => !!p);
  const withFacts = fill(`${name} – `, facts, ', ', SEO_TITLE_MAX) ?? name;
  return withFacts.length + BRAND.length <= SEO_TITLE_MAX ? withFacts + BRAND : withFacts;
}

export function eventSeoDescription(e: SeoSnippetEvent): string {
  const parts = [
    statusLabel(e),
    e.permanent ? null : longDate(parseDay(e.eventDate), lastDay(e)),
    location(e),
    distancesLabel(e),
    elevationLabel(e),
    registrationLabel(e),
    nonEmpty(e.organizer) ? `Veranstalter: ${nonEmpty(e.organizer)}` : null,
  ].filter((p): p is string => !!p);
  const filled = fill('', parts, ' • ', SEO_DESCRIPTION_MAX);
  if (filled) return filled;
  const first = parts[0];
  return first.length > SEO_DESCRIPTION_MAX ? `${first.slice(0, SEO_DESCRIPTION_MAX - 1)}…` : first;
}
